import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Send, Search, Plus, Users, User, Megaphone, MoreVertical, Pin, Paperclip, Check, CheckCheck, X, MessageSquare, Settings, Smile, Image as ImageIcon, FileText, Loader2, UserPlus, LogOut, Download, Crown, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const Messenger = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [chatType, setChatType] = useState('direct');
  const [participantSearch, setParticipantSearch] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [wsConnection, setWsConnection] = useState(null);
  const [isTyping, setIsTyping] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  // Attachment + group-info state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [groupDetail, setGroupDetail] = useState(null);
  const [groupMemberSearch, setGroupMemberSearch] = useState('');
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const unreadMapRef = useRef(null);        // conv_id -> last-seen unread_count (null = first load)
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await apiClient.get('/auth/me');
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);
  
  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await apiClient.get('/messenger/conversations');
      const convs = response.data.conversations || [];
      setConversations(convs);

      // Calculate total unread
      const total = convs.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadCount(total);

      // Unread tracking only — user-facing notifications are handled globally by
      // useMessageNotifications() so they also fire on non-Messenger pages.
      const nextMap = {};
      convs.forEach((c) => { nextMap[c.conversation_id] = c.unread_count || 0; });
      unreadMapRef.current = nextMap;
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Keep a ref of the open conversation for the WS handler + polling, and
  // expose it globally so useMessageNotifications can suppress pings for the
  // chat you are currently viewing (while the app is focused).
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
    try { window.__openConversationId = selectedConversation?.conversation_id || null; } catch (_) {}
  }, [selectedConversation]);

  // Clear the global open-conversation marker when leaving the Messenger page.
  useEffect(() => () => { try { window.__openConversationId = null; } catch (_) {} }, []);

  // Polling fallback — guarantees messages arrive on the logged-in device even
  // if the WebSocket is down or the user appears "offline". Also re-syncs the
  // authoritative (DB-backed) online set so the online dots stay consistent
  // across backend pods instead of flapping.
  const refreshOnlineUsers = useCallback(() => {
    apiClient.get('/messenger/online-users')
      .then((res) => setOnlineUsers((res.data.online_users || []).map((u) => u.user_id)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    refreshOnlineUsers();
    const interval = setInterval(() => {
      fetchConversations();
      refreshOnlineUsers();
      const open = selectedConversationRef.current;
      if (open) {
        apiClient.get(`/messenger/conversations/${open.conversation_id}/messages`)
          .then((res) => {
            const msgs = res.data.messages || [];
            setMessages((prev) => (msgs.length !== prev.length ? msgs : prev));
          })
          .catch(() => {});
      }
    }, 7000);
    return () => clearInterval(interval);
  }, [currentUser, fetchConversations, refreshOnlineUsers]);
  
  // WebSocket connection (with auto-reconnect)
  useEffect(() => {
    if (!currentUser) return;

    let closedByUs = false;

    const connect = () => {
      // Auth token lives in localStorage (Bearer), NOT a cookie.
      const token = localStorage.getItem('session_token');
      if (!token) return;

      const base = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      const siteId = localStorage.getItem('active_site_id') || 'salem';
      const ws = new WebSocket(`${base}/api/messenger/ws/${token}?site_id=${encodeURIComponent(siteId)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnection(ws);
        // Heartbeat: keep this user's server-side presence fresh so the
        // online dot doesn't flap (presence is DB-backed, read every poll).
        if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(JSON.stringify({ type: 'ping' })); } catch (_) { /* ignore */ }
          }
        }, 20000);
      };

      ws.onmessage = (event) => {
        try { handleWebSocketMessage(JSON.parse(event.data)); } catch (_) { /* ignore */ }
      };

      ws.onerror = () => { /* onclose will handle reconnect */ };

      ws.onclose = () => {
        setWsConnection(null);
        if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null; }
        if (!closedByUs) {
          // Auto-reconnect so a dropped socket doesn't leave the user "offline".
          reconnectTimerRef.current = setTimeout(connect, 4000);
        }
      };
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null; }
      if (wsRef.current) { try { wsRef.current.close(); } catch (_) {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  
  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    switch (data.type) {
      case 'connected':
        setOnlineUsers(data.online_users || []);
        setUnreadCount(data.unread_count || 0);
        break;
      
      case 'new_message':
        // Add message to current conversation if it matches (use ref to avoid
        // a stale closure from the WS effect).
        if (selectedConversationRef.current?.conversation_id === data.message.conversation_id) {
          setMessages(prev => (prev.some(m => m.message_id === data.message.message_id) ? prev : [...prev, data.message]));
        }
        // Refresh conversations to update last message + unread.
        // (The toast/desktop/chime notification is fired globally by
        // useMessageNotifications so it is not duplicated here.)
        fetchConversations();
        break;
      
      case 'typing':
        if (data.is_typing) {
          setIsTyping(prev => ({ ...prev, [data.user_id]: true }));
        } else {
          setIsTyping(prev => {
            const newState = { ...prev };
            delete newState[data.user_id];
            return newState;
          });
        }
        break;
      
      case 'presence':
        if (data.status === 'online') {
          setOnlineUsers(prev => [...new Set([...prev, data.user_id])]);
        } else {
          setOnlineUsers(prev => prev.filter(id => id !== data.user_id));
        }
        break;
      
      case 'message_read':
        // Update read status
        break;
      
      default:
        break;
    }
  };
  
  // Fetch messages for a conversation
  const fetchMessages = async (conversationId) => {
    try {
      const response = await apiClient.get(`/messenger/conversations/${conversationId}/messages`);
      setMessages(response.data.messages || []);
      
      // Mark as read
      await apiClient.post(`/messenger/conversations/${conversationId}/read`);
      fetchConversations(); // Refresh to update unread counts
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    }
  };
  
  // Select a conversation
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    await fetchMessages(conversation.conversation_id);
    
    // Subscribe to conversation via WebSocket
    if (wsConnection) {
      wsConnection.send(JSON.stringify({
        type: 'subscribe',
        conversation_id: conversation.conversation_id
      }));
    }
  };

  // Deep-link: open a conversation from ?conversation=<id> (e.g. clicking a
  // desktop message notification). Runs once the conversations list is loaded.
  const deepLinkedRef = useRef(null);
  useEffect(() => {
    const target = searchParams.get('conversation');
    if (!target || conversations.length === 0) return;
    if (deepLinkedRef.current === target) return;
    const conv = conversations.find((c) => c.conversation_id === target);
    if (conv) {
      deepLinkedRef.current = target;
      handleSelectConversation(conv);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversations]);
  
  // Send a message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !selectedConversation || isSending) return;
    
    setIsSending(true);
    try {
      const response = await apiClient.post(
        `/messenger/conversations/${selectedConversation.conversation_id}/messages`,
        { content: newMessage.trim() }
      );
      
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
      messageInputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };
  
  // Attachment upload + send
  const handleAttachClick = () => {
    if (!selectedConversation || uploadingFile) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !selectedConversation) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await apiClient.post(
        `/messenger/conversations/${selectedConversation.conversation_id}/attachments`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const res = await apiClient.post(
        `/messenger/conversations/${selectedConversation.conversation_id}/messages`,
        { content: newMessage.trim(), attachments: [up.data] }
      );
      setMessages(prev => [...prev, res.data.message]);
      setNewMessage('');
      messageInputRef.current?.focus();
    } catch (error) {
      console.error('Error sending attachment:', error);
      toast.error(error.response?.data?.detail || 'Failed to send attachment');
    } finally {
      setUploadingFile(false);
    }
  };
  
  // Handle typing indicator
  const handleTyping = () => {
    if (!wsConnection || !selectedConversation) return;
    
    wsConnection.send(JSON.stringify({
      type: 'typing',
      conversation_id: selectedConversation.conversation_id,
      is_typing: true
    }));
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      wsConnection.send(JSON.stringify({
        type: 'typing',
        conversation_id: selectedConversation.conversation_id,
        is_typing: false
      }));
    }, 2000);
  };
  
  // Fetch users for new conversation
  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/messenger/users');
      setUsers(response.data.users?.filter(u => u.user_id !== currentUser?.user_id) || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  // Create new conversation
  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }
    
    try {
      const request = {
        type: chatType,
        participant_ids: selectedUsers.map(u => u.user_id),
        name: chatType === 'group' ? newGroupName : undefined
      };
      
      const response = await apiClient.post('/messenger/conversations', request);
      
      setShowNewChat(false);
      setSelectedUsers([]);
      setNewGroupName('');
      
      // Refresh and select the new conversation
      await fetchConversations();
      
      const newConv = await apiClient.get(`/messenger/conversations/${response.data.conversation_id}`);
      handleSelectConversation(newConv.data);
      
      toast.success(response.data.existing ? 'Opened existing conversation' : 'Conversation created');
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Failed to create conversation');
    }
  };
  
  // Group info / member management
  const openGroupInfo = async () => {
    if (!selectedConversation) return;
    setShowGroupInfo(true);
    setShowAddMembers(false);
    setGroupMemberSearch('');
    try {
      const res = await apiClient.get(`/messenger/conversations/${selectedConversation.conversation_id}`);
      setGroupDetail(res.data);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to load group info');
    }
  };

  const isGroupAdmin = () => {
    if (!groupDetail || !currentUser) return false;
    return (groupDetail.admins || []).includes(currentUser.user_id) || groupDetail.created_by === currentUser.user_id;
  };

  const handleAddMember = async (userId) => {
    if (!selectedConversation) return;
    setSavingMembers(true);
    try {
      const res = await apiClient.post(
        `/messenger/conversations/${selectedConversation.conversation_id}/participants`,
        { user_ids: [userId] }
      );
      setGroupDetail(prev => ({ ...prev, participants: res.data.participants, participants_info: res.data.participants_info, admins: res.data.admins, created_by: res.data.created_by }));
      fetchConversations();
      toast.success('Member added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setSavingMembers(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedConversation) return;
    const leaving = userId === currentUser?.user_id;
    setSavingMembers(true);
    try {
      const res = await apiClient.delete(
        `/messenger/conversations/${selectedConversation.conversation_id}/participants/${userId}`
      );
      if (leaving) {
        setShowGroupInfo(false);
        setSelectedConversation(null);
        fetchConversations();
        toast.success('You left the group');
        return;
      }
      setGroupDetail(prev => ({ ...prev, participants: res.data.participants, participants_info: res.data.participants_info, admins: res.data.admins, created_by: res.data.created_by }));
      fetchConversations();
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    } finally {
      setSavingMembers(false);
    }
  };
  
  // Pin message
  const handlePinMessage = async (messageId) => {
    try {
      const response = await apiClient.post(`/messenger/messages/${messageId}/pin`);
      toast.success(response.data.message);
      fetchMessages(selectedConversation.conversation_id);
    } catch (error) {
      toast.error('Failed to pin message');
    }
  };
  
  // Format timestamp — stored values are UTC (naive ISO from the backend),
  // displayed in IST (Asia/Kolkata) regardless of the device timezone.
  const IST_TZ = 'Asia/Kolkata';
  const parseUTC = (ts) => {
    if (!ts) return null;
    const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(ts);
    const d = new Date(hasTz ? ts : ts + 'Z');
    return isNaN(d.getTime()) ? null : d;
  };
  const istYMD = (d) => new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
  const formatTime = (timestamp) => {
    const date = parseUTC(timestamp);
    if (!date) return '';
    const now = new Date();
    const dYMD = istYMD(date);
    const nYMD = istYMD(now);
    if (dYMD === nYMD) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: IST_TZ });
    }
    const diffDays = Math.round((Date.parse(nYMD) - Date.parse(dYMD)) / 86400000);
    if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-IN', { weekday: 'short', timeZone: IST_TZ });
    }
    return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', timeZone: IST_TZ });
  };
  
  // Get conversation display name
  const getConversationName = (conv) => {
    if (conv.name) return conv.name;
    if (conv.type === 'direct') {
      const other = conv.participants_info?.find(p => p.user_id !== currentUser?.user_id);
      return other?.name || 'Unknown';
    }
    return 'Group Chat';
  };
  
  // Get conversation avatar initials
  const getAvatarInitials = (conv) => {
    const name = getConversationName(conv);
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  // Filter conversations by search
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = getConversationName(conv).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="h-screen flex flex-col bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg flex-shrink-0">
        <div className="max-w-full mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-400" />
              <h1 className="text-lg font-bold">Internal Messenger</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-green-500/20 text-green-300">
              {onlineUsers.length} Online
            </Badge>
            {unreadCount > 0 && (
              <Badge variant="destructive">
                {unreadCount} Unread
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversations Sidebar */}
        <div className="w-80 border-r border-[#E5DFD0] bg-white flex flex-col">
          {/* Search & New Chat */}
          <div className="p-3 border-b border-[#E5DFD0] space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="pl-9"
              />
            </div>
            <Dialog open={showNewChat} onOpenChange={(open) => { setShowNewChat(open); setParticipantSearch(''); if (open) { fetchUsers(); } else { setSelectedUsers([]); setNewGroupName(''); } }}>
              <DialogTrigger asChild>
                <Button className="w-full bg-[#2D5F4F] hover:bg-[#234A3D]">
                  <Plus className="w-4 h-4 mr-2" />
                  New Conversation
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden">
                <DialogHeader>
                  <DialogTitle>Start New Conversation</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 flex flex-col flex-1 min-h-0 overflow-hidden">
                  <Tabs value={chatType} onValueChange={setChatType}>
                    <TabsList className="grid grid-cols-3">
                      <TabsTrigger value="direct"><User className="w-4 h-4 mr-1" />Direct</TabsTrigger>
                      <TabsTrigger value="group"><Users className="w-4 h-4 mr-1" />Group</TabsTrigger>
                      <TabsTrigger value="broadcast"><Megaphone className="w-4 h-4 mr-1" />Broadcast</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  
                  {chatType !== 'direct' && (
                    <Input
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder={chatType === 'group' ? 'Group name...' : 'Broadcast name...'}
                    />
                  )}
                  
                  <div className="space-y-2 flex flex-col flex-1 min-h-0">
                    <label className="text-sm font-medium">Select Participants</label>
                    <div className="relative">
                      <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <Input
                        value={participantSearch}
                        onChange={(e) => setParticipantSearch(e.target.value)}
                        placeholder="Search by name, staff ID, email or role..."
                        className="pl-8"
                        data-testid="participant-search-input"
                      />
                    </div>
                    <ScrollArea className="flex-1 min-h-[160px] border rounded-md p-2">
                      {(() => {
                        const q = participantSearch.trim().toLowerCase();
                        const idLabel = (u) => u.staff_id || u.email || u.user_id;
                        const list = q
                          ? users.filter(u =>
                              (u.name || '').toLowerCase().includes(q) ||
                              (u.role || '').toLowerCase().includes(q) ||
                              (u.staff_id || '').toLowerCase().includes(q) ||
                              (u.email || '').toLowerCase().includes(q) ||
                              (u.user_id || '').toLowerCase().includes(q))
                          : users;
                        if (list.length === 0) {
                          return <p className="text-sm text-gray-400 text-center py-6" data-testid="participant-empty">No users found</p>;
                        }
                        return list.map(user => (
                        <div
                          key={user.user_id}
                          data-testid={`participant-option-${user.user_id}`}
                          className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                            selectedUsers.find(u => u.user_id === user.user_id)
                              ? 'bg-[#2D5F4F]/10'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            if (chatType === 'direct') {
                              setSelectedUsers([user]);
                            } else {
                              setSelectedUsers(prev => 
                                prev.find(u => u.user_id === user.user_id)
                                  ? prev.filter(u => u.user_id !== user.user_id)
                                  : [...prev, user]
                              );
                            }
                          }}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-[#2D5F4F] text-white text-xs">
                              {user.name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {user.name}
                              <span className="ml-1.5 text-xs font-normal text-gray-400">· {idLabel(user)}</span>
                            </p>
                            <p className="text-xs text-gray-500 truncate">{user.role}</p>
                          </div>
                          {selectedUsers.find(u => u.user_id === user.user_id) && (
                            <Check className="w-4 h-4 text-[#2D5F4F]" />
                          )}
                        </div>
                        ));
                      })()}
                    </ScrollArea>
                  </div>
                  
                  <Button
                    onClick={handleCreateConversation}
                    disabled={selectedUsers.length === 0}
                    className="w-full shrink-0 bg-[#2D5F4F] hover:bg-[#234A3D]"
                  >
                    Create Conversation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          {/* Conversation List */}
          <ScrollArea className="flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-[#2D5F4F]" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
              </div>
            ) : (
              filteredConversations.map(conv => (
                <div
                  key={conv.conversation_id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`flex items-center gap-3 p-3 cursor-pointer border-b border-[#E5DFD0] transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id
                      ? 'bg-[#2D5F4F]/10'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="relative">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className={`text-white text-sm ${
                        conv.type === 'group' ? 'bg-blue-500' :
                        conv.type === 'broadcast' ? 'bg-purple-500' : 'bg-[#2D5F4F]'
                      }`}>
                        {conv.type === 'group' ? <Users className="w-5 h-5" /> :
                         conv.type === 'broadcast' ? <Megaphone className="w-5 h-5" /> :
                         getAvatarInitials(conv)}
                      </AvatarFallback>
                    </Avatar>
                    {conv.is_online && conv.type === 'direct' && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm truncate">{getConversationName(conv)}</p>
                      <span className="text-xs text-gray-400">{formatTime(conv.last_message_at)}</span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {conv.last_sender_name && conv.type !== 'direct' && `${conv.last_sender_name}: `}
                      {conv.last_message || 'No messages yet'}
                    </p>
                  </div>
                  {conv.unread_count > 0 && (
                    <Badge className="bg-red-500 text-white">{conv.unread_count}</Badge>
                  )}
                  {conv.is_pinned && <Pin className="w-4 h-4 text-amber-500" />}
                </div>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col bg-[#F8F6F0]">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="h-14 px-4 flex items-center justify-between border-b border-[#E5DFD0] bg-white">
                <div
                  className={`flex items-center gap-3 ${selectedConversation.type !== 'direct' ? 'cursor-pointer' : ''}`}
                  onClick={() => { if (selectedConversation.type !== 'direct') openGroupInfo(); }}
                  data-testid="messenger-chat-header"
                >
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className={`text-white text-sm ${
                      selectedConversation.type === 'group' ? 'bg-blue-500' :
                      selectedConversation.type === 'broadcast' ? 'bg-purple-500' : 'bg-[#2D5F4F]'
                    }`}>
                      {getAvatarInitials(selectedConversation)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-sm">{getConversationName(selectedConversation)}</p>
                    <p className="text-xs text-gray-500">
                      {selectedConversation.type === 'direct' 
                        ? (selectedConversation.is_online ? 'Online' : 'Offline')
                        : `${selectedConversation.participants?.length || 0} members · tap for info`
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon">
                    <Search className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {selectedConversation.type !== 'direct' && (
                        <DropdownMenuItem onClick={openGroupInfo} data-testid="messenger-group-info-menu">
                          <Users className="w-4 h-4 mr-2" /> Group info
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem>View Pinned Messages</DropdownMenuItem>
                      <DropdownMenuItem>Mute Notifications</DropdownMenuItem>
                      {selectedConversation.type !== 'direct' && (
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => currentUser && handleRemoveMember(currentUser.user_id)}
                          data-testid="messenger-leave-group"
                        >
                          <LogOut className="w-4 h-4 mr-2" /> Leave Group
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4 max-w-3xl mx-auto">
                  {messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === currentUser?.user_id;
                    const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                    
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex gap-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        {!isOwn && showAvatar && (
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            <AvatarFallback className="bg-[#2D5F4F] text-white text-xs">
                              {msg.sender_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        {!isOwn && !showAvatar && <div className="w-8" />}
                        
                        <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                          {!isOwn && showAvatar && (
                            <p className="text-xs text-gray-500 mb-1 ml-1">{msg.sender_name}</p>
                          )}
                          <div
                            className={`rounded-2xl px-4 py-2 ${
                              isOwn 
                                ? 'bg-[#2D5F4F] text-white rounded-br-md' 
                                : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                            }`}
                          >
                            {msg.pinned && (
                              <div className="flex items-center gap-1 text-xs mb-1 opacity-70">
                                <Pin className="w-3 h-3" />
                                Pinned
                              </div>
                            )}
                            {(msg.attachments || []).map((att, ai) => {
                              const src = att.signed_url || att.url;
                              const isImg = att.is_image || (att.content_type || '').startsWith('image/');
                              if (isImg && src) {
                                return (
                                  <a key={att.attachment_id || ai} href={src} target="_blank" rel="noreferrer" className="block mb-1" data-testid="messenger-attachment-image">
                                    <img src={src} alt={att.filename || 'image'}
                                      className={msg.linked_entity?.type === 'hourly_report'
                                        ? 'w-[560px] max-w-full rounded-lg border border-gray-200 bg-white object-contain'
                                        : 'max-w-[240px] max-h-[260px] rounded-lg object-cover'} />
                                  </a>
                                );
                              }
                              return (
                                <a
                                  key={att.attachment_id || ai}
                                  href={src}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`flex items-center gap-2 mb-1 px-3 py-2 rounded-lg ${isOwn ? 'bg-white/15' : 'bg-gray-100'}`}
                                  data-testid="messenger-attachment-file"
                                >
                                  <FileText className="w-4 h-4 flex-shrink-0" />
                                  <span className="text-xs truncate max-w-[180px]">{att.filename || 'file'}</span>
                                  <Download className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
                                </a>
                              );
                            })}
                            {msg.content ? (
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                            ) : null}
                            <div className={`flex items-center gap-1 mt-1 text-xs ${isOwn ? 'text-white/70 justify-end' : 'text-gray-400'}`}>
                              <span>{formatTime(msg.created_at)}</span>
                              {isOwn && (
                                msg.read_by?.length > 1 
                                  ? <CheckCheck className="w-3 h-3" /> 
                                  : <Check className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                          
                          {/* Message actions */}
                          <div className={`flex gap-1 mt-1 opacity-0 hover:opacity-100 transition-opacity ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="w-6 h-6"
                              onClick={() => handlePinMessage(msg.message_id)}
                            >
                              <Pin className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Typing indicator */}
                  {Object.keys(isTyping).length > 0 && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span>Someone is typing...</span>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="p-4 bg-white border-t border-[#E5DFD0]">
                <form onSubmit={handleSendMessage} className="flex items-end gap-2 max-w-3xl mx-auto">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelected}
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    data-testid="messenger-file-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0"
                    onClick={handleAttachClick}
                    disabled={uploadingFile}
                    title="Attach a file"
                    data-testid="messenger-attach-btn"
                  >
                    {uploadingFile ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                  </Button>
                  <div className="flex-1 relative">
                    <Textarea
                      ref={messageInputRef}
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Type a message..."
                      className="min-h-[44px] max-h-32 resize-none pr-10"
                      rows={1}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={!newMessage.trim() || isSending}
                    className="flex-shrink-0 bg-[#2D5F4F] hover:bg-[#234A3D]"
                  >
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            // No conversation selected
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium text-gray-700">Select a conversation</h3>
                <p className="text-sm">Choose from existing chats or start a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Group Info / Members dialog */}
      <Dialog open={showGroupInfo} onOpenChange={(o) => { setShowGroupInfo(o); if (!o) { setShowAddMembers(false); setGroupMemberSearch(''); } }}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col overflow-hidden" data-testid="messenger-group-info-dialog">
          <DialogHeader>
            <DialogTitle>{showAddMembers ? 'Add members' : 'Group info'}</DialogTitle>
          </DialogHeader>

          {!showAddMembers ? (
            <div className="flex-1 overflow-y-auto">
              <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-blue-500 text-white">
                    {selectedConversation ? getAvatarInitials(selectedConversation) : ''}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedConversation ? getConversationName(selectedConversation) : ''}</p>
                  <p className="text-xs text-gray-500">{(groupDetail?.participants_info || []).length} members</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 mb-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Members</p>
                {isGroupAdmin() && (
                  <Button size="sm" variant="ghost" className="h-7 text-[#2D5F4F]" onClick={() => { setShowAddMembers(true); setGroupMemberSearch(''); }} data-testid="messenger-add-members-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Add
                  </Button>
                )}
              </div>

              <div className="space-y-1">
                {(groupDetail?.participants_info || []).map((m) => {
                  const isAdm = (groupDetail?.admins || []).includes(m.user_id) || groupDetail?.created_by === m.user_id;
                  const online = onlineUsers.includes(m.user_id);
                  return (
                    <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50" data-testid={`messenger-member-${m.user_id}`}>
                      <div className="relative">
                        <Avatar className="w-9 h-9">
                          <AvatarFallback className="bg-[#2D5F4F] text-white text-xs">{m.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        {online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{m.name}{m.user_id === currentUser?.user_id ? ' (You)' : ''}</p>
                        <p className="text-xs text-gray-400 truncate">{m.role || m.email}</p>
                      </div>
                      {isAdm && <Badge variant="secondary" className="text-[10px]"><Crown className="w-3 h-3 mr-1" />Admin</Badge>}
                      {isGroupAdmin() && m.user_id !== currentUser?.user_id && (
                        <Button size="icon" variant="ghost" className="w-7 h-7 text-red-500 hover:text-red-600" disabled={savingMembers} onClick={() => handleRemoveMember(m.user_id)} data-testid={`messenger-remove-member-${m.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              <Button variant="ghost" className="w-full mt-3 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => currentUser && handleRemoveMember(currentUser.user_id)} data-testid="messenger-leave-group-dialog">
                <LogOut className="w-4 h-4 mr-2" /> Leave group
              </Button>
            </div>
          ) : (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search people..." value={groupMemberSearch} onChange={(e) => setGroupMemberSearch(e.target.value)} className="pl-9" data-testid="messenger-add-member-search" />
              </div>
              <div className="flex-1 overflow-y-auto space-y-1">
                {users
                  .filter(u => !(groupDetail?.participants || []).includes(u.user_id))
                  .filter(u => !groupMemberSearch || (u.name || '').toLowerCase().includes(groupMemberSearch.toLowerCase()))
                  .map(u => (
                    <div key={u.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                      <Avatar className="w-9 h-9"><AvatarFallback className="bg-gray-400 text-white text-xs">{u.name?.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        <p className="text-xs text-gray-400 truncate">{u.role || u.email}</p>
                      </div>
                      <Button size="sm" variant="outline" disabled={savingMembers} onClick={() => handleAddMember(u.user_id)} data-testid={`messenger-add-user-${u.user_id}`}>
                        <UserPlus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                  ))}
                {users.filter(u => !(groupDetail?.participants || []).includes(u.user_id)).length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-6">Everyone is already in this group</p>
                )}
              </div>
              <Button variant="ghost" className="mt-2" onClick={() => setShowAddMembers(false)} data-testid="messenger-add-members-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to members
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messenger;
